/**
 * Validation patterns for onboarding forms
 * Centralized regex patterns for consistent validation across the application
 */

export const VALIDATION_PATTERNS = {
  /**
   * Email validation pattern
   * Matches standard email format: username@domain.tld
   */
  EMAIL: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,

  /**
   * Norwegian phone number pattern
   * Matches:
   * - 8 digits starting with 4-9: 45678901
   * - +47 followed by 8 digits starting with 4-9: +47 45678901 or +4745678901
   * - Must start with 4-9 (Norwegian mobile numbers)
   */
  PHONE_NO: /^((\+47\s?)?[4-9]\d{7})$/,

  /**
   * Password validation pattern
   * Requirements:
   * - Minimum 8 characters
   * - At least one letter (a-zA-Z)
   * - At least one digit (0-9)
   * - At least one special character
   */
  PASSWORD: /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]).{8,}$/,
};

/**
 * Validation rules constants
 */
export const VALIDATION_RULES = {
  USERNAME_MIN_LENGTH: 2,
  PASSWORD_MIN_LENGTH: 8,
};

/**
 * Validates Norwegian account number using modulo-11 algorithm
 * @param {string} accountNo - Account number to validate (can include spaces/dots)
 * @returns {string} Error message if invalid, empty string if valid
 *
 * Norwegian account numbers are 11 digits with modulo-11 check digit validation
 * Weight sequence: 5432765432
 */
export const validateAccountNumber = (accountNo) => {
  if (!accountNo) {
    return "Kontonummer er påkrevd";
  }

  // Remove any spaces or dots
  const cleaned = accountNo.replace(/[\s.]/g, "");

  // Must be exactly 11 digits
  if (!/^\d{11}$/.test(cleaned)) {
    return "Kontonummer må være 11 siffer";
  }

  // Modulo-11 validation with weight sequence 5432765432
  const weights = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
  const digits = cleaned.split("").map(Number);

  // Calculate weighted sum of first 10 digits
  let sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += digits[i] * weights[i];
  }

  // Calculate check digit
  const remainder = sum % 11;
  let checkDigit = 11 - remainder;

  // If remainder is 0, check digit is 0
  if (remainder === 0) {
    checkDigit = 0;
  }

  // If check digit is 10 or 11, the account number is invalid
  if (checkDigit >= 10) {
    return "Kontonummer har ugyldig kontrollsiffer";
  }

  // Validate against the 11th digit
  if (checkDigit !== digits[10]) {
    return "Kontonummer har ugyldig kontrollsiffer";
  }

  return ""; // Valid
};
